import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { DemoModeProvider } from "@/contexts/DemoModeContext";

// Eagerly-loaded: critical paths (auth + main shells)
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AppLayout from "@/layouts/AppLayout";
import PortalLayout from "@/layouts/PortalLayout";
import Dashboard from "@/pages/Dashboard"; // landing page after login → keep eager

// Lazy-loaded: every other route (split into separate chunks)
const Countries = lazy(() => import("@/pages/Countries"));
const Sectors = lazy(() => import("@/pages/Sectors"));
const Companies = lazy(() => import("@/pages/Companies"));
const CompanyDetail = lazy(() => import("@/pages/CompanyDetail"));
const Principals = lazy(() => import("@/pages/Principals"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const AIAgent = lazy(() => import("@/pages/AIAgent"));
const Reports = lazy(() => import("@/pages/Reports"));
const Settings = lazy(() => import("@/pages/Settings"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const ImportExport = lazy(() => import("@/pages/ImportExport"));
const ImportMandants = lazy(() => import("@/pages/ImportMandants"));
const ImportMandantsBatch = lazy(() => import("@/pages/ImportMandantsBatch"));
const Digest = lazy(() => import("@/pages/Digest"));
const DiscoverCompanies = lazy(() => import("@/pages/DiscoverCompanies"));
const EmailApprovals = lazy(() => import("@/pages/EmailApprovals"));
const EmailPerformance = lazy(() => import("@/pages/EmailPerformance"));
const AICoachHistory = lazy(() => import("@/pages/AICoachHistory"));
const DataPrep = lazy(() => import("@/pages/DataPrep"));
const QualityAudit = lazy(() => import("@/pages/QualityAudit"));
const MarketIntel = lazy(() => import("@/pages/MarketIntel"));
const ApiCredits = lazy(() => import("@/pages/ApiCredits"));
const PrivateDocs = lazy(() => import("@/pages/PrivateDocs"));
const Missions = lazy(() => import("@/pages/Missions"));
const MandantOnboarding = lazy(() => import("@/pages/MandantOnboarding"));
const ReplyCoach = lazy(() => import("@/pages/ReplyCoach"));

// Phase 5A — Principal Viewer Portal (auth-isolated)
const PortalLogin = lazy(() => import("@/pages/portal/PortalLogin"));
const PortalHome = lazy(() => import("@/pages/portal/PortalHome"));
const PortalProspects = lazy(() => import("@/pages/portal/PortalProspects"));
const PortalActivities = lazy(() => import("@/pages/portal/PortalActivities"));
const PortalDigests = lazy(() => import("@/pages/portal/PortalDigests"));

function PageFallback() {
  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center text-muted-foreground">
      <div className="text-sm">Chargement…</div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-muted-foreground">
        <div className="text-sm">Chargement…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if ((user?.role || "").toUpperCase() !== "ADMIN") {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <DemoModeProvider>
            <Suspense fallback={<PageFallback />}>
              <Routes>
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />

              {/* Phase 5A — Principal Viewer Portal (separate auth, no admin gate) */}
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/portal" element={<PortalLayout />}>
                <Route index element={<Navigate to="/portal/home" replace />} />
                <Route path="home" element={<PortalHome />} />
                <Route path="prospects" element={<PortalProspects />} />
                <Route path="activities" element={<PortalActivities />} />
                <Route path="digests" element={<PortalDigests />} />
              </Route>
              <Route
                path="/"
                element={
                  <Protected>
                    <AppLayout />
                  </Protected>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="countries" element={<AdminOnly><Countries /></AdminOnly>} />
                <Route path="sectors" element={<AdminOnly><Sectors /></AdminOnly>} />
                <Route path="companies" element={<Companies />} />
                <Route path="companies/:id" element={<CompanyDetail />} />
                <Route path="principals" element={<Principals />} />
                <Route path="contacts" element={<Contacts />} />
                <Route path="pipeline" element={<Pipeline />} />
                <Route path="ai-agent" element={<AIAgent />} />
                <Route path="reports" element={<Reports />} />
                <Route path="settings" element={<Settings />} />
                <Route path="users" element={<AdminOnly><UsersPage /></AdminOnly>} />
                <Route path="import-export" element={<AdminOnly><ImportExport /></AdminOnly>} />
                <Route path="import-mandants" element={<AdminOnly><ImportMandants /></AdminOnly>} />
                <Route path="import-mandants/batch/:id" element={<AdminOnly><ImportMandantsBatch /></AdminOnly>} />
                <Route path="digest" element={<Digest />} />
                <Route path="digest/:id" element={<Digest />} />
                <Route path="discover" element={<DiscoverCompanies />} />
                <Route path="approvals" element={<EmailApprovals />} />
                <Route path="email-performance" element={<EmailPerformance />} />
                <Route path="coach" element={<AICoachHistory />} />
                <Route path="reply-coach" element={<ReplyCoach />} />
                <Route path="quality-audit" element={<QualityAudit />} />
                <Route path="market-intel" element={<MarketIntel />} />
                <Route path="credits" element={<AdminOnly><ApiCredits /></AdminOnly>} />
                <Route path="private-docs" element={<AdminOnly><PrivateDocs /></AdminOnly>} />
                <Route path="missions" element={<AdminOnly><Missions /></AdminOnly>} />
                <Route path="onboarding-mandant" element={<AdminOnly><MandantOnboarding /></AdminOnly>} />
                <Route path="data-prep" element={<AdminOnly><DataPrep /></AdminOnly>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </DemoModeProvider>
        </BrowserRouter>
        <Toaster
          position="top-right"
          theme="system"
          toastOptions={{
            className: "border border-border bg-card text-card-foreground",
          }}
        />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
